import { PDFPage, PDFFont, StandardFonts, rgb, type Color } from 'pdf-lib';

import { StampingTheme } from './models';

const MIN_OPACITY = 0.05;
const MAX_OPACITY = 1;
const STROKE_WIDTH = 1;

export type StampRenderRequest = {
  page: PDFPage;
  text: string;
  /** Box origin x (bottom-left of the stamp box), in PDF points. */
  x: number;
  /** Box origin y (bottom-left of the stamp box), in PDF points. */
  y: number;
  /** Box width including horizontal padding on both sides. */
  width: number;
  /** Box height including vertical padding on top and bottom. */
  height: number;
  opacity: number;
  theme: StampingTheme;
  fontSize?: number;
  horizontalPadding: number;
  verticalPadding: number;
};

interface ThemeStyle {
  text: Color;
  background: Color;
  stroke: Color | null;
}

/**
 * Theme presets, matching the Mac CoreGraphicsTextRenderer contract:
 * a filled background box behind the text, plus an optional stroke for the
 * `…WithStroke` presets. Without the background box the stamp would render as
 * bare text (and `whiteOnBlack*` text would be invisible on a light page).
 */
function themeStyle(theme: StampingTheme): ThemeStyle {
  const black = rgb(0, 0, 0);
  const white = rgb(1, 1, 1);

  switch (theme) {
    case 'whiteOnBlack':
      return { text: white, background: black, stroke: null };
    case 'blackOnWhiteWithStroke':
      return { text: black, background: white, stroke: black };
    case 'whiteOnBlackWithStroke':
      return { text: white, background: black, stroke: white };
    case 'blackOnWhite':
    default:
      return { text: black, background: white, stroke: null };
  }
}

export async function renderStampText({
  page,
  text,
  x,
  y,
  width,
  height,
  opacity,
  theme,
  fontSize = 12,
  horizontalPadding,
  verticalPadding,
}: StampRenderRequest): Promise<void> {
  const document = page.doc;
  const font = await document.embedFont(StandardFonts.Helvetica);
  const clampedOpacity = clampOpacity(opacity);
  const style = themeStyle(theme);

  // 1. Draw the background fill box (+ optional stroke). The Mac renderer uses
  //    a 3pt corner radius; pdf-lib has no native rounded rectangle, so the box
  //    is drawn with square corners. The radius is small and only visible on
  //    the non-default themes (which the Mac CLI can't emit for cross-engine
  //    diffing), so this is a documented minor visual divergence.
  page.drawRectangle({
    x,
    y,
    width,
    height,
    color: style.background,
    opacity: clampedOpacity,
    ...(style.stroke
      ? { borderColor: style.stroke, borderWidth: STROKE_WIDTH, borderOpacity: clampedOpacity }
      : {}),
  });

  // 2. Draw the text on top, inset by the padding. pdf-lib's drawText places
  //    the baseline at y, so the top line's baseline sits one ascent below the
  //    box top (minus the top padding); subsequent lines step down by the line
  //    height. The bottom line ends one descent above the bottom padding.
  const lines = text.split('\n');
  const lineHeight = font.heightAtSize(fontSize);
  const descent = lineHeight - font.heightAtSize(fontSize, { descender: false });
  let baselineY = y + height - verticalPadding - (lineHeight - descent);

  for (const line of lines) {
    page.drawText(line, {
      x: x + horizontalPadding,
      y: baselineY,
      size: fontSize,
      font,
      color: style.text,
      opacity: clampedOpacity,
    });
    baselineY -= lineHeight;
  }
}

function clampOpacity(opacity: number): number {
  return Math.min(Math.max(opacity, MIN_OPACITY), MAX_OPACITY);
}

export function fontNameFromFont(font: PDFFont): StandardFonts | null {
  const value = font.name;

  if (value.includes('Helvetica')) {
    return StandardFonts.Helvetica;
  }

  return null;
}
